import { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Volleyball, CreditCard, Phone, UploadSimple, CheckCircle, Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ZELLE_PHONE = "5618596830";

export function CourtRentalPortal({ user }) {
  const { t, i18n } = useTranslation();
  const [info, setInfo] = useState({ price_student: 40 });
  const [form, setForm] = useState({ phone: '', date: '', start_time: '', end_time: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('form');
  const [rentalId, setRentalId] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/court-rental/info`).then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.phone || !form.date || !form.start_time || !form.end_time) {
      return toast.error(t('courtRental.missingFields'));
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/portal/court-rental/register`, form, { withCredentials: true });
      setRentalId(data.id);
      setStep('choose-payment');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('courtRental.registerError'));
    } finally {
      setLoading(false);
    }
  };

  const payCard = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/portal/court-rental/checkout`, { rental_id: rentalId, origin_url: window.location.origin }, { withCredentials: true });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t('courtRental.checkoutError'));
      setLoading(false);
    }
  };

  const uploadZelle = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/portal/court-rental/zelle-receipt?rental_id=${rentalId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true });
      toast.success(t('common.receiptSent'));
      setStep('success');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('portal.receiptSendError'));
    } finally {
      setUploading(false);
    }
  };

  const totalCard = ((info.price_student || 40) * 1.05).toFixed(2);
  const resetFlow = () => {
    setStep('form');
    setForm({ phone: '', date: '', start_time: '', end_time: '', notes: '' });
    setRentalId(null);
  };

  return (
    <div className="bg-white border border-[#E6E2D8] rounded-lg overflow-hidden" data-testid="court-rental-portal-card">
      <div className="h-24 bg-gradient-to-br from-[#0077B6] to-[#03045E] relative">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute bottom-0 left-0 p-4 text-white flex items-center gap-2">
          <Volleyball size={26} weight="fill" />
          <div>
            <h2 className="font-['Outfit'] text-xl font-bold leading-tight">{t('courtRental.title')}</h2>
            <p className="text-xs opacity-90">${info.price_student} - {i18n.language === 'en' ? 'students only' : 'exclusivo para alunos'}</p>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-4">
        {step === 'form' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="crp-phone" className="text-xs">{t('courtRental.fields.phone')} *</Label>
                <Input id="crp-phone" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="(305) 123-4567" data-testid="court-rental-portal-phone" />
              </div>
              <div>
                <Label htmlFor="crp-date" className="text-xs">{t('courtRental.fields.date')} *</Label>
                <Input id="crp-date" type="date" value={form.date} onChange={(e) => update('date', e.target.value)} data-testid="court-rental-portal-date" />
              </div>
              <div>
                <Label htmlFor="crp-start" className="text-xs">{t('courtRental.fields.start')} *</Label>
                <Input id="crp-start" type="time" value={form.start_time} onChange={(e) => update('start_time', e.target.value)} data-testid="court-rental-portal-start" />
              </div>
              <div>
                <Label htmlFor="crp-end" className="text-xs">{t('courtRental.fields.end')} *</Label>
                <Input id="crp-end" type="time" value={form.end_time} onChange={(e) => update('end_time', e.target.value)} data-testid="court-rental-portal-end" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="crp-notes" className="text-xs">{t('courtRental.fields.notes')}</Label>
                <Textarea id="crp-notes" rows={2} value={form.notes} onChange={(e) => update('notes', e.target.value)} data-testid="court-rental-portal-notes" />
              </div>
            </div>
            <Button onClick={submit} disabled={loading} className="w-full bg-[#E85D04] hover:bg-[#DC2F02] text-white py-3" data-testid="court-rental-portal-submit">
              {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Volleyball size={18} className="mr-2" />}
              {t('courtRental.reserveAndPay')}
            </Button>
          </>
        )}

        {step === 'choose-payment' && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
              <CheckCircle size={20} className="text-[#2D6A4F] mt-0.5" weight="fill" />
              <div>
                <p className="text-sm font-semibold text-[#1A1A1A]">{t('courtRental.reservedTitle')}</p>
                <p className="text-xs text-[#73706A]"><Clock size={12} className="inline" /> {form.date} {form.start_time} - {form.end_time}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button onClick={payCard} disabled={loading} className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white py-4 flex-col h-auto" data-testid="court-rental-portal-pay-card">
                <div className="flex items-center"><CreditCard size={20} className="mr-2" /> {t('common.card')}</div>
                <span className="text-xs opacity-90 mt-1">${totalCard} (5%)</span>
              </Button>
              <Button onClick={() => setStep('zelle')} variant="outline" className="border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white py-4 flex-col h-auto" data-testid="court-rental-portal-zelle">
                <div className="flex items-center"><Phone size={20} className="mr-2" /> Zelle</div>
                <span className="text-xs mt-1">${info.price_student}</span>
              </Button>
            </div>
          </div>
        )}

        {step === 'zelle' && (
          <div className="space-y-4 text-center">
            <div className="w-16 h-16 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto"><Phone size={28} className="text-white" /></div>
            <p className="text-[#73706A]">{t('courtRental.zelleHeader')} <strong className="text-[#E85D04]">${info.price_student}</strong> {t('courtRental.zelleHeaderSuffix')}</p>
            <div className="bg-[#F5F2EB] rounded-lg p-4">
              <p className="text-2xl font-bold text-[#1A1A1A] tracking-wider">{ZELLE_PHONE}</p>
            </div>
            <label className="cursor-pointer block">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => uploadZelle(e.target.files[0])} data-testid="court-rental-portal-zelle-upload" />
              <Button className="w-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white pointer-events-none" disabled={uploading}>
                {uploading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <UploadSimple size={18} className="mr-2" />}
                {uploading ? t('common.sending') : t('common.uploadReceiptShort')}
              </Button>
            </label>
          </div>
        )}

        {step === 'success' && (
          <div className="text-center py-6 space-y-3">
            <div className="w-16 h-16 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto"><CheckCircle size={32} className="text-white" weight="fill" /></div>
            <h2 className="font-['Outfit'] text-xl font-bold">{t('courtRental.confirmedTitle')}</h2>
            <Button onClick={resetFlow} variant="outline">{t('common.newRegistration')}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
